import appConfig from '../../config/appConfig';
import {IS_PARALLEL_FETCH_TOKEN} from '../../consts/app/yapInitialState';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as logMessageTypes from '../../consts/log/logMessageTypes';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapModeTypes from '../../consts/yap/yapModeTypes';
import {setFetchTokenInProgress} from '../../state/ducks/auth';
import {
    setDataCollectionLastRequestId,
    setSyncLastRequestId,
    setYapLastRequestId,
} from '../../state/ducks/yapEncrypted';
import {selectIsFetchTokenInProgress} from '../../state/selectors/auth';
import {
    makeSelectAssetIdByType,
    makeSelectDataCollectionLastRequestId,
    makeSelectIsHolderByAssetId,
    makeSelectSyncLastRequestId,
    makeSelectYapLastRequestId,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import backendService from '../app/backendService';
import gamClientService from '../gam/gamClientService';
import log from '../logger/log';
import uamClientService from '../uam/uamClientService';

const getYapMode = (type) => appConfig.getYapMode() === type;

const isYapEncryptedModeAndP4 = (isP4) => (isP4 ? isYapEncryptedMode() : false);
const isYapEncryptedMode = () => getYapMode(yapModeTypes.YAP_ENCRYPTED_MODE);
const isYapMode = () => !getYapMode(yapModeTypes.YAP_DISABLED_MODE);

const isDataCollectionEnabled = () => appConfig.getIsDataCollectionEnabled();

const proceedAction = ({status, requestId, assetId, isSync = false, isDataCollection = false}) => {
    const isPending = status === yapActionStatusTypes.PENDING;

    if (isPending) return;

    const state = getState();
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);
    const holderRequestId = makeSelectYapLastRequestId(true)(state);
    const deviceRequestId = makeSelectYapLastRequestId()(state);
    const curRequestId = isHolder ? holderRequestId : deviceRequestId;
    const lastRequestId = isDataCollection
        ? makeSelectDataCollectionLastRequestId(isHolder)(state)
        : isSync
        ? makeSelectSyncLastRequestId(isHolder)(state)
        : curRequestId;

    if (requestId !== lastRequestId) {
        log.info(
            `WebSocketService: answer for requestId ${requestId} is ignored. Waiting for ${lastRequestId}. isHolder: ${isHolder}`
        );
        return false;
    }

    const isFailed = status === yapActionStatusTypes.FAILED;
    const isSuccess = status === yapActionStatusTypes.SUCCESS;
    const isCanceled = status === yapActionStatusTypes.CANCELED;

    if (!isFailed && !isSuccess && !isCanceled) return;

    if (isSync) {
        dispatch(setSyncLastRequestId(null, isHolder));
    } else if (isDataCollection) {
        dispatch(setDataCollectionLastRequestId(null, isHolder));
    } else {
        dispatch(setYapLastRequestId(null, isHolder));
    }

    return status;
};

const getSyncAsset = async (isHolder) => {
    let assetId = makeSelectAssetIdByType(isHolder)(getState());

    if (assetId) return assetId;

    await waitUntilFetchTokenFinish(isHolder);

    const isGamBackend = backendService.isGamBackend();
    let isValid;

    if (isGamBackend) {
        isValid = await gamClientService.initGam(isHolder);
    } else {
        isValid = await uamClientService.getUamTokenAndAssets(isHolder);
    }
    if (isValid) {
        assetId = makeSelectAssetIdByType(isHolder)(getState());
    }
    if (isValid && assetId) return assetId;
    log.error(
        `yapService: Asset does not exist for registered device isHolder: ${!!isHolder}.`,
        isGamBackend ? logMessageTypes.GAM : logMessageTypes.UAM
    );

    //due fast state change timeout is added
    await helpers.timeout(500);
    return null;
};

const waitUntilFetchTokenFinish = async (isHolder, timer = uamTimeouts.UAM_WAIT_FETCH_TOKEN_TIMEOUT) => {
    if (IS_PARALLEL_FETCH_TOKEN) return;
    const isGamBackend = backendService.isGamBackend();
    const isFetchTokenInProgress = selectIsFetchTokenInProgress(getState());

    if (!isFetchTokenInProgress) {
        dispatch(setFetchTokenInProgress(true));
        return;
    }

    if (--timer < 0) {
        log.info(
            `yapService: Does not receive token:  ${!!isHolder}.`,
            isGamBackend ? logMessageTypes.GAM : logMessageTypes.UAM
        );
        return;
    }

    await helpers.timeout(1000);
    return waitUntilFetchTokenFinish(timer);
};

export default {
    getSyncAsset,
    isDataCollectionEnabled,
    isYapEncryptedMode,
    isYapEncryptedModeAndP4,
    isYapMode,
    proceedAction,
};
